import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from 'Core/components/Icon/Icon';

const OTP_LENGTH = 6;
const ALNUM = /^[A-Za-z0-9]$/;
const RESEND_COOLDOWN_SECONDS = 90;
const RESEND_TS_KEY = 'fortiva.otp.resendStartedAt';

const computeSecondsLeft = () => {
  const startedAt = Number(sessionStorage.getItem(RESEND_TS_KEY));
  if (!startedAt) return 0;
  const elapsed = Math.floor((Date.now() - startedAt) / 1000);
  return Math.max(0, RESEND_COOLDOWN_SECONDS - elapsed);
};

const sanitize = (str) =>
  (str || '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, OTP_LENGTH);

export default function OtpInput({
  value = '',
  onChange,
  maskedEmail = '',
  error = '',
  onResend,
  isResending = false,
}) {
  const { t } = useTranslation();
  const refs = React.useRef([]);
  const chars = value.padEnd(OTP_LENGTH, ' ').split('');

  const [secondsLeft, setSecondsLeft] = React.useState(computeSecondsLeft);
  const intervalRef = React.useRef(null);

  const startTicking = React.useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      const left = computeSecondsLeft();
      setSecondsLeft(left);
      if (left <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 1000);
  }, []);

  // Resume the countdown after a page refresh (timestamp lives in sessionStorage).
  React.useEffect(() => {
    if (computeSecondsLeft() > 0) startTicking();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [startTicking]);

  const handleResendClick = () => {
    if (isResending || secondsLeft > 0) return;
    sessionStorage.setItem(RESEND_TS_KEY, String(Date.now()));
    setSecondsLeft(RESEND_COOLDOWN_SECONDS);
    startTicking();
    onResend();
  };

  const resendDisabled = isResending || secondsLeft > 0;

  const setAt = (idx, next) => {
    const merged = chars.slice();
    merged[idx] = next || ' ';
    onChange(sanitize(merged.join('')));
  };

  const handleChange = (idx) => (e) => {
    const raw = e.target.value;
    if (raw.length > 1) {
      const cleaned = sanitize(raw);
      onChange(cleaned);
      const focusIdx = Math.min(cleaned.length, OTP_LENGTH - 1);
      refs.current[focusIdx]?.focus();
      return;
    }
    if (raw === '' || ALNUM.test(raw)) {
      setAt(idx, raw.toUpperCase());
      if (raw && idx < OTP_LENGTH - 1) refs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx) => (e) => {
    if (e.key === 'Backspace' && !chars[idx].trim() && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < OTP_LENGTH - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = sanitize(e.clipboardData.getData('text'));
    onChange(pasted);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    refs.current[focusIdx]?.focus();
  };

  return (
    <div className="rounded-xl border border-slate-300 bg-gradient-to-br from-slate-50 to-blue-50/40 p-5">
      <h4 className="!mt-0 !mb-1 text-sm font-bold text-gray-900 text-center">
        {t('fortiva.finalize.otp.heading')}
      </h4>
      <p className="!mt-0 !mb-3 text-xs text-gray-600 text-center">
        {t('fortiva.finalize.otp.subheading', { maskedEmail })}
      </p>
      <div className="flex flex-col items-center gap-3">
        <div className="flex gap-2">
          {Array.from({ length: OTP_LENGTH }).map((_, i) => (
            <input
              key={i}
              ref={(el) => (refs.current[i] = el)}
              value={chars[i].trim()}
              maxLength={OTP_LENGTH}
              inputMode="text"
              autoComplete="one-time-code"
              data-testid={`otp-input-${i}`}
              onChange={handleChange(i)}
              onKeyDown={handleKeyDown(i)}
              onPaste={handlePaste}
              aria-invalid={Boolean(error)}
              className={classnames(
                'w-10 h-12 text-center text-lg font-mono uppercase',
                'border rounded',
                'focus:outline-none focus:ring-2',
                error
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                  : 'border-slate-300 focus:ring-fortiva-bg focus:border-fortiva-bg'
              )}
            />
          ))}
        </div>
        {onResend && (
          <div className="flex flex-col items-center gap-1">
            <button
              type="button"
              onClick={handleResendClick}
              disabled={resendDisabled}
              data-testid="otp-resend-button"
              className={classnames(
                'inline-flex items-center gap-2 text-sm font-medium underline underline-offset-2',
                'focus:outline-none focus:ring-2 focus:ring-fortiva-bg rounded',
                resendDisabled
                  ? 'text-slate-400 cursor-not-allowed no-underline'
                  : 'text-fortiva-bg hover:text-fortiva-bg/80 cursor-pointer'
              )}
            >
              {isResending && <Icon type="spinner" className="!h-4 !w-4" />}
              {secondsLeft > 0
                ? t('fortiva.finalize.otp.resendCountdown', {
                    seconds: secondsLeft,
                    defaultValue: 'Re-send OTP in {{seconds}}s',
                  })
                : t('fortiva.finalize.otp.resend')}
            </button>
            <p
              className="!mt-0 !mb-0 text-xs text-gray-500 text-center"
              data-testid="otp-resend-note"
            >
              {t('fortiva.finalize.otp.resendWaitNote', {
                defaultValue:
                  'Please wait a few minutes for the code to arrive before requesting a new one.',
              })}
            </p>
          </div>
        )}
      </div>
      {error && (
        <p
          className="mt-2 text-xs text-red-600 text-center"
          role="alert"
          data-testid="otp-input-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}
